<template>
    <b-modal
        :title="screeningTitle"
        :visible="true"
        centered
        header-bg-variant="success"
        header-text-variant="light"
        @hide="closeModal"
        @ok="updateOldTraffickingScreening"
    >
        <b-row>
            <b-col>
                <div>
                    <b>
                        Screening Results
                    </b>
                </div>
                {{ screeningConclusion }}
                <p class="mt-3">
                    <b>
                        If available, upload PDF copy of the Louisiana Indicator Tool for Child & Youth Sex Trafficking by clicking on the button below.
                    </b>
                </p>
                <div v-if="!screening.fileName || isFileDeleted">
                    <label for="file-upload" class="custom-file-upload">
                        Upload PDF
                    </label>
                    <span>{{ updatedScreening.fileName }}</span>
                    <input :disabled="isLoading" type="file" id="file-upload" @change="onFileUpload" />
                </div>
                <div v-else>
                    <div v-if="downloadLink">
                        <a :href="downloadLink" :download="screening.fileName">
                            {{ screening.fileName }}
                        </a>
                        <button class="clear-btn" @click="deleteScreeningFile">
                            <i class="icon-trash"></i>
                        </button>
                    </div>
                    <pulse-loader color="#489AAC" size="20px" v-else></pulse-loader>
                </div>
                <p class="mt-3">
                    <b>Comments</b>
                </p>
                <b-form-textarea rows="10" class="mt-3" placeholder="Enter Comments" name="comments" v-model="updatedScreening.comments"></b-form-textarea>
            </b-col>
            <b-col>
                <b-card class="mt-3" no-body>
                    <b-card-body>
                        <p class="card-text">
                            <small class="text-muted text-uppercase font-weight-bold">First Recorded</small>
                            <br/>
                            <small class="text-muted"><b> on: </b> {{ (screening.createdAt/1000) | unixToHuman }}
                            <div v-if="_.isObject(screening.createdBy)">
                                <b> by: </b> {{ _.get(screening, 'createdBy.emailAddress', '') }}
                            </div>
                            </small>
                        </p>
                        <p class="card-text">
                            <small class="text-muted text-uppercase font-weight-bold">Last Updated</small>
                            <br/>
                            <small class="text-muted"><b> on: </b> {{ (screening.updatedAt/1000) | unixToHuman }}
                            <div v-if="_.isObject(screening.updatedBy)">
                                <b> by: </b> {{ _.get(screening, 'updatedBy.emailAddress', '') }}
                            </div>
                            </small>
                        </p>
                        <p class="card-text">
                            <small class="text-muted text-uppercase font-weight-bold">Current Record Owner</small>
                            <br/>
                            <small class="text-muted"><b> by: </b> {{ _.get(screening, 'organizationOwner.title') }} </small>
                        </p>
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>
        <b-alert
            fade
            :show="updateOldTraffickingScreeningError || updateOldTraffickingScreeningSuccess || downloadOldTraffickingScreeningError"
            :variant="updateOldTraffickingScreeningSuccess ? 'success' : 'danger'"
        >
            <span v-html="apiResponseMessage"></span>
        </b-alert>
        <template #modal-footer>
            <div class="w-100">
                <b-button
                    variant="primary"
                    size="sm"
                    class="float-right"
                    style="width: 60px"
                    :disabled="isLoading"
                    @click="updateOldTraffickingScreening"
                >
                    <i v-if="isLoading" class="fa fa-spinner"></i>
                    <span v-else>
                        Update
                    </span>
                </b-button>
                <b-button
                    variant="link"
                    size="sm"
                    class="float-right"
                    :disabled="isLoading"
                    @click="closeModal"
                >
                    Cancel
                </b-button>
            </div>
        </template>
    </b-modal>
</template>

<script>
import { mapGetters } from 'vuex'
import { OLD_TRAFFICKING_SCREENING_CONCLUSION_LABEL_MAP } from '@/helpers/enums'

export default {
    name: 'ViewOldTraffickingScreeningModal',

    props: {
        screening: {
            type: Object,
            default () {
                return {}
            }
        }
    },

    data () {
        return {
            updatedScreening: {},
            isLoading: false,
            isFileDeleted: false,
            closeModalTimeout: null,
            downloadLink: ''
        }
    },

    computed: {
        ...mapGetters({
            updateOldTraffickingScreeningError: 'getUpdateOldTraffickingScreeningError',
            updateOldTraffickingScreeningSuccess: 'getUpdateOldTraffickingScreeningSuccess',
            downloadOldTraffickingScreeningError: 'getDownloadOldTraffickingScreeningError',
            oldTraffickingScreeningFile: 'getOldTraffickingScreeningFile'
        }),

        screeningTitle () {
            const date = this.$moment(this.screening.createdAt).format('MM/DD/YYYY')
            return `${this.screening.id} - ${date}`
        },

        screeningConclusion () {
            return OLD_TRAFFICKING_SCREENING_CONCLUSION_LABEL_MAP[this.screening.conclusion]
        },

        apiResponseMessage () {
            const message = this.updateOldTraffickingScreeningSuccess
                ? 'Success'
                : 'An error occurred. Please try again.'

            return `<p>${message}</p>`
        }
    },

    watch: {
        screening: {
            handler (newValue) {
                // console.log('newValue', newValue)
                this.getScreeningFileToDownload()
                this.updatedScreening = Object.assign({}, newValue)
            },
            deep: true,
            immediate: true
        },

        updateOldTraffickingScreeningSuccess (value) {
            if (value) {
                this.closeModalTimeout = setTimeout(() => {
                    this.closeModal()
                }, 500)
            }
        },

        updateOldTraffickingScreeningError (value) {
            if (value) {
                this.closeModalTimeout = setTimeout(() => {
                    this.closeModal()
                }, 500)
            }
        },

        oldTraffickingScreeningFile: {
            handler (value) {
                if (!value) {
                    return
                }

                const data = Buffer.from(value['Body'].data)
                const blob = new Blob([data], { type: value['ContentType'] })
                this.downloadLink = URL.createObjectURL(blob)
            },
            deep: true,
            immediate: true
        }
    },

    beforeDestroy () {
        clearTimeout(this.closeModalTimeout)
    },

    methods: {
        closeModal () {
            this.$store.commit('SET_OLD_TRAFFICKING_SCREENING_FILE', null)
            this.downloadLink = null
            this.$emit('close')
        },

        updateOldTraffickingScreening () {
            this.isLoading = true
            this.$store.dispatch('updateOldTraffickingScreening', this.updatedScreening)
        },

        deleteScreeningFile () {
            this.updatedScreening.fileName = ''
            this.updatedScreening.fileData = ''
            this.isFileDeleted = true
        },

        onFileUpload (e) {
            if (!e.target.files.length) {
                return
            }

            const fileData = e.target.files[0]

            this.updatedScreening.fileName = fileData.name
            this.updatedScreening.fileData = fileData
        },

        getScreeningFileToDownload () {
            if (this.oldTraffickingScreeningFile || !this.screening.fileName) {
                return
            }

            this.$store.dispatch('downloadOldTraffickingScreening', {
                _id: this.screening._id
            })
        }
    }
}
</script>

<style lang="scss" scoped>
input[type="file"] {
    position: absolute;
    left: -99999rem;
}

.custom-file-upload {
    display: inline-block;
    font-weight: 400;
    color: #fff;
    text-align: center;
    vertical-align: middle;
    cursor: pointer;
    background-color: #4dbd74;
    border: 1px solid #4dbd74;
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.5;
    border-radius: 0;
    -webkit-transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;
}

.clear-btn {
    background: none;
	color: inherit;
	border: none;
	padding: 0;
	font: inherit;
	cursor: pointer;
	outline: inherit;
}

.download-screening-btn {
    color: #20a8d8;
    text-decoration: none;
    background-color: transparent;
}

a:hover {
    color: #167495;
    text-decoration: underline;
}

.fa-spinner {
    animation: spin 5000ms infinite linear;
}

@keyframes spin {
    from {
        transform:rotate(0deg);
    }
    to {
        transform:rotate(360deg);
    }
}
</style>


// WEBPACK FOOTER //
// src/views/t1/ViewOldTraffickingScreeningModal.vue